import React, { useState, useRef } from 'react';
import { ColorResult } from 'react-color';
import classNames from 'classnames';
import Switch from '../../../general-components/switch';
import SelectSmall from '../../../general-components/select-small';
import { SelectGroupOption } from '../../../general-components/select-group';
import ColorPicker from '../color-picker';
import PixelInput from '../../../general-components/pixel-input';
import { formatHex } from '../../../../utils/utils';
import { AppSettings } from '../../../../stores/reducers/settings';

interface Props {
	settingsForShotEdited: (newQueue: any) => void;
	queue: AppSettings;
}

const options: SelectGroupOption[] = [
	{
		label: 'Inside',
		value: 'inside',
	},
	{
		label: 'Outside',
		value: 'outside',
	},
];

const opacityOptions: SelectGroupOption[] = [
	{ label: '100%', value: 1 },
	{ label: '75%', value: 0.75 },
	{ label: '50%', value: 0.5 },
	{ label: '25%', value: 0.25 },
];

export default function VideoBorder(props: Props) {
	const {
		queue,
		settingsForShotEdited
	 } = props;

	const [fullColor, setFullColor] = useState<any>({ hex: queue.presenterVideoBorderColor });
	const [showBorderColor, setShowBorderColor] = useState(false);
	const colorDebounce = useRef<NodeJS.Timeout | null>(null);

	function changePresenterVideoBorderOn(e: React.ChangeEvent<HTMLInputElement>) {
		const newQueue = { ...queue, presenterVideoBorderOn: e.target.checked };
		settingsForShotEdited(newQueue);
	}

	function changeBorderPlacement(placement: string) {
		const newQueue = { ...queue, presenterVideoBorderPosition: placement };
		settingsForShotEdited(newQueue);
	}

	function changeBorderSize(size: number) {
		const newQueue = { ...queue, presenterVideoBorderSize: size };
		settingsForShotEdited(newQueue);
	}

	function changeBorderOpacity(opacity: number) {
		const newQueue = { ...queue, presenterVideoBorderOpacity: opacity };
		settingsForShotEdited(newQueue);
	}

	function closeColorPicker() {
		setShowBorderColor(false);
	}

	function colorChange(color: ColorResult) {
		setFullColor(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, presenterVideoBorderColor: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function toggleColorPicker() {
		setShowBorderColor(!showBorderColor);
	}

	return (
		<div className='settings-container presenter-video-border-container'>
			<div className="align-items-center justify-content-space-between">
				<label className="subsection-title">Presenter Video Border</label>
				<Switch
					on={queue.presenterVideoBorderOn}
					onChange={changePresenterVideoBorderOn}
					className={'green'}
				/>
			</div>
			<div className={classNames('border-options', { disabled: !queue.presenterVideoBorderOn })}>
				<SelectSmall
					options={options}
					onChange={changeBorderPlacement}
					label={queue.presenterVideoBorderPosition || options[0].label}
					selected={options.find(
						(option) => option.value === queue.presenterVideoBorderPosition,
					) || options[0]}
					dropdown={true}
				/>
				<div className={'border-size'}>
					<PixelInput
						onChange={changeBorderSize}
						value={queue.presenterVideoBorderSize}
						disablePxVisibility
					/>
				</div>

				<div className="color-group">
					<div className="color-toggle" onClick={toggleColorPicker}>
						<div className="color-swatch" style={{ backgroundColor: queue.presenterVideoBorderColor }}></div>
						<span className="format-hex">{queue.presenterVideoBorderColor}</span>
					</div>

					<SelectSmall
						options={opacityOptions}
						onChange={changeBorderOpacity}
						label={opacityOptions.find(
							(option) => option.value === queue.presenterVideoBorderOpacity,
						)?.label || opacityOptions[0].label}
						selected={opacityOptions.find(
							(option) => option.value === queue.presenterVideoBorderOpacity,
						) || opacityOptions[0]}
						dropdownPosition={'drop-left'}
					/>
				</div>

				<ColorPicker
					value={formatHex(fullColor.hex)}
					color={fullColor}
					label={'Add color'}
					open={showBorderColor}
					close={closeColorPicker}
					onChange={colorChange}
				/>
			</div>

		</div>
	);
}
